import React from "react";
import { Flame } from "lucide-react";
import { fmtNum, fmtUsd } from "@/lib/format";

// Colourful positions & exposure heatmap: tile SIZE reflects notional exposure,
// tile COLOR reflects PNL (green profit / red loss), intensity by magnitude.
export function PositionsHeatmap({ positions = [] }) {
  if (!positions || positions.length === 0) {
    return (
      <div className="rounded-md border border-[#262626] bg-[#141414]" data-testid="positions-heatmap">
        <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2">
          <Flame className="w-4 h-4 text-[#D4AF37]" />
          <span className="font-heading font-semibold text-[#F5F5F5] text-sm">Positions Heatmap</span>
        </div>
        <div className="h-32 flex items-center justify-center text-sm text-[#525252]">No exposure to visualise.</div>
      </div>
    );
  }

  const enriched = positions.map((p) => ({
    ...p,
    exposure: Math.abs(p.size) * (p.mark_price || p.entry_price || 1),
  }));
  const maxExp = Math.max(...enriched.map((p) => p.exposure), 1);
  const maxAbsPnl = Math.max(...enriched.map((p) => Math.abs(p.pnl || 0)), 1);

  const tileStyle = (p) => {
    const intensity = 0.16 + 0.55 * Math.min(Math.abs(p.pnl || 0) / maxAbsPnl, 1);
    const bg = (p.pnl || 0) >= 0 ? `rgba(16,185,129,${intensity})` : `rgba(239,68,68,${intensity})`;
    const grow = 1 + 4 * (p.exposure / maxExp); // 1..5
    return { background: bg, flexGrow: grow, flexBasis: `${90 + 60 * (p.exposure / maxExp)}px` };
  };

  return (
    <div className="rounded-md border border-[#262626] bg-[#141414]" data-testid="positions-heatmap">
      <div className="px-4 py-3 border-b border-[#262626] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-[#D4AF37]" />
          <span className="font-heading font-semibold text-[#F5F5F5] text-sm">Positions Heatmap</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#737373]">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[rgba(16,185,129,0.6)]" /> Profit</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[rgba(239,68,68,0.6)]" /> Loss</span>
          <span className="hidden sm:inline">· size ∝ exposure</span>
        </div>
      </div>
      <div className="p-3 flex flex-wrap gap-2" data-testid="heatmap-grid">
        {enriched.map((p, i) => (
          <div
            key={i}
            data-testid={`heatmap-tile-${i}`}
            className="rounded-sm border border-white/5 p-3 min-h-[84px] flex flex-col justify-between transition-transform hover:scale-[1.02] hover:border-white/20"
            style={tileStyle(p)}
            title={`${p.symbol} ${p.side} · Exposure ${fmtUsd(p.exposure)} · PNL ${fmtNum(p.pnl)}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono font-semibold text-sm text-white">{p.symbol}</span>
              <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-sm ${p.side === "LONG" ? "bg-black/30 text-[#10B981]" : "bg-black/30 text-[#EF4444]"}`}>{p.side}</span>
            </div>
            <div>
              <div className={`font-mono text-base font-bold ${(p.pnl || 0) >= 0 ? "text-white" : "text-white"}`}>{fmtNum(p.pnl)}</div>
              <div className="font-mono text-[10px] text-white/70 mt-0.5">{p.leverage}x · {fmtUsd(p.exposure, 0)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
